import { useRef, useState } from "react";
import { IconChevronDown, IconDownload, IconFileImport, IconFileSpreadsheet } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Group,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { exportarExcel } from "@/shared/export/exportar-excel";
import { toastAdvertencia } from "@/lib/toast";
import {
  importarMapeosClaveCuenta,
  type FilaImportacionMapeo,
  type PreviewFilaMapeo,
  type ResultadoImportacionMapeo,
} from "../services/mapeos-clave-cuenta-service";
import { useImportarMapeosClaveCuenta } from "../hooks/use-mapeos-clave-cuenta-mutations";

/** Encabezados aceptados por columna (normalizados: minúsculas, sin espacios). */
const CLAVE_HEADERS = ["clave", "clavesat", "clavesatsat"];
const CUENTA_HEADERS = ["cuenta", "cuentacodigo", "codigo", "codigocuenta"];
const NOMBRE_HEADERS = ["nombre", "descripcion"];

const norm = (s: string) => s.trim().toLowerCase().replace(/[\s._-]/g, "");

/**
 * Descarga la plantilla .xlsx con los encabezados EXACTOS que espera el
 * importador ("Clave SAT", "Cuenta", "Nombre") y un par de filas de ejemplo.
 */
function descargarPlantilla() {
  exportarExcel<{ claveSat: string; cuenta: string; nombre: string }>({
    archivo: "plantilla-cuentas-por-clave",
    hoja: "Cuentas por clave",
    columnas: [
      { header: "Clave SAT", value: (f) => f.claveSat },
      { header: "Cuenta", value: (f) => f.cuenta },
      { header: "Nombre", value: (f) => f.nombre },
    ],
    filas: [
      { claveSat: "80121600", cuenta: "2219010100", nombre: "Servicios legales (ejemplo · reemplázame)" },
      { claveSat: "84111506", cuenta: "6201", nombre: "Papelería (ejemplo · reemplázame)" },
    ],
  });
}

function extraer(fila: Record<string, unknown>, candidatos: string[]): string {
  for (const [k, v] of Object.entries(fila)) {
    if (candidatos.includes(norm(k))) return String(v ?? "").trim();
  }
  return "";
}

/**
 * Importación masiva desde Excel con VISTA PREVIA: parsea la primera hoja en el
 * cliente, la manda al backend en dry-run (`soloValidar`) para saber qué se hará
 * con cada fila (crear/actualizar/omitir), muestra un modal de preview y sólo al
 * confirmar persiste de verdad.
 */
export function MapeosImportarExcel() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filas, setFilas] = useState<FilaImportacionMapeo[]>([]);
  const [preview, setPreview] = useState<ResultadoImportacionMapeo | null>(null);

  // Dry-run (no persiste) · abre el modal con el detalle por fila.
  const validar = useMutation({
    mutationFn: (fs: FilaImportacionMapeo[]) => importarMapeosClaveCuenta(fs, true),
    onSuccess: (res) => setPreview(res),
    onError: () => toastAdvertencia({ title: "No se pudo validar el archivo", message: "Intenta de nuevo." }),
  });

  const cerrar = () => {
    setPreview(null);
    setFilas([]);
  };
  // Import real (persiste) · al terminar cierra el modal. La mutation muestra el toast.
  const importar = useImportarMapeosClaveCuenta({ onImportado: cerrar });

  async function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        toastAdvertencia({ title: "Archivo vacío", message: "El Excel no tiene hojas con datos." });
        return;
      }
      const crudas = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
      const parsed: FilaImportacionMapeo[] = crudas
        .map((r) => ({
          claveSat: extraer(r, CLAVE_HEADERS).toUpperCase(),
          cuentaCodigo: extraer(r, CUENTA_HEADERS),
          nombre: extraer(r, NOMBRE_HEADERS),
        }))
        .filter((f) => f.claveSat && f.cuentaCodigo);

      if (parsed.length === 0) {
        toastAdvertencia({
          title: "Sin filas válidas",
          message: "Revisa que existan las columnas Clave (SAT) y Cuenta (código) con datos.",
        });
        return;
      }
      setFilas(parsed);
      validar.mutate(parsed);
    } catch {
      toastAdvertencia({ title: "No se pudo leer el archivo", message: "¿Es un .xlsx/.xls válido?" });
    }
  }

  const aImportar = preview ? preview.creados + preview.actualizados : 0;

  return (
    <>
      {/* Un solo control "Excel" que unifica importar + descargar plantilla. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            leftSection={<IconFileSpreadsheet size={14} />}
            rightSection={<IconChevronDown size={14} />}
            loading={validar.isPending || importar.isPending}
          >
            Excel
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            <IconFileImport size={16} stroke={1.7} />
            Importar datos en Excel
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={descargarPlantilla}>
            <IconDownload size={16} stroke={1.7} />
            Descargar plantilla
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => void onArchivo(e)}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />

      <Dialog open={preview !== null} onOpenChange={(o) => !o && cerrar()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Vista previa de importación</DialogTitle>
          </DialogHeader>
          {preview && (
            <Stack gap="sm">
              <Group gap="xs">
                <Badge color="exito">{preview.creados} a crear</Badge>
                <Badge color="info">{preview.actualizados} a actualizar</Badge>
                <Badge color={preview.omitidos > 0 ? "peligro" : "slate"}>{preview.omitidos} a omitir</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {aImportar > 0
                  ? "Revisa las filas antes de confirmar. Sólo se aplicarán las de crear/actualizar; las omitidas se ignoran."
                  : "Ninguna fila es importable (revisa los motivos). Corrige el archivo y vuelve a intentar."}
              </Text>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
                <Table verticalSpacing="xs" horizontalSpacing="sm">
                  <Thead>
                    <Tr>
                      <Th>#</Th>
                      <Th>Clave SAT</Th>
                      <Th>Cuenta</Th>
                      <Th>Nombre</Th>
                      <Th>Acción</Th>
                      <Th>Motivo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {preview.detalle.map((f) => (
                      <FilaPreview key={`${f.renglon}-${f.claveSat}-${f.cuentaCodigo}`} fila={f} />
                    ))}
                  </Tbody>
                </Table>
              </div>
            </Stack>
          )}
          <DialogFooter>
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button
              loading={importar.isPending}
              disabled={aImportar === 0}
              onClick={() => importar.mutate(filas)}
            >
              Confirmar importación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const COLOR_ESTADO: Record<PreviewFilaMapeo["estado"], string> = {
  Crear: "exito",
  Actualizar: "info",
  Omitir: "peligro",
};

function FilaPreview({ fila }: { fila: PreviewFilaMapeo }) {
  return (
    <Tr>
      <Td className="font-mono">{fila.renglon}</Td>
      <Td>
        <Text size="xs" ff="mono">
          {fila.claveSat}
        </Text>
      </Td>
      <Td>
        <Text size="xs" ff="mono">
          {fila.cuentaCodigo}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{fila.nombre || "—"}</Text>
      </Td>
      <Td>
        <Badge size="sm" color={COLOR_ESTADO[fila.estado]}>
          {fila.estado}
        </Badge>
      </Td>
      <Td>
        <Text size="xs" c={fila.motivo ? "peligro" : "dimmed"}>
          {fila.motivo ?? "—"}
        </Text>
      </Td>
    </Tr>
  );
}
